import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { parseDateFromDatePicker } from 'services/DateService';

import type { IHomeworkAssignment } from 'core';

interface IHomeworkAssignmentCard {
  assignment: IHomeworkAssignment;
  className?: string;
}

const URL_RE = /(https?:\/\/[^\s]+)/g;

/**
 * The assignment text is rendered as PLAIN text (never as HTML) — React
 * escapes it, so teacher-authored content cannot inject markup. Bare URLs are
 * linkified by splitting the string, which keeps every fragment escaped.
 */
const renderTextWithLinks = (text: string) =>
  text.split(URL_RE).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <a key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  );

/** Read-only assignment block: title, informational due date, safe text. */
export const HomeworkAssignmentCard = memo(({ assignment, className }: IHomeworkAssignmentCard) => {
  const { t } = useTranslation();

  return (
    <Wrapper className={className}>
      <Title>{assignment.title || t('homework.assignment.title')}</Title>
      {assignment.dueDate && (
        <DueDate>
          {t('homework.assignment.due', {
            date: parseDateFromDatePicker(DateTime.fromISO(assignment.dueDate).toJSDate()),
          })}
        </DueDate>
      )}
      {/* rev.3: with no teacher-authored assignment the brief lives in the
          lesson materials — say so instead of rendering an empty block. */}
      {assignment.hasTeacherAssignment ? (
        <Text>{renderTextWithLinks(assignment.text)}</Text>
      ) : (
        <Text>{t('homework.assignment.seeInMaterials')}</Text>
      )}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  background-color: ${Colors.bg_gray};
  border-radius: 8px;
  padding: 20px;

  ${tablet} {
    padding: 24px;
  }
`;

const Title = styled.h3`
  margin: 0 0 8px;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const DueDate = styled.p`
  margin: 0 0 12px;
  color: ${Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const Text = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;
